import * as THREE from 'three';

/** Octagon (chamfered rectangle) — the signature silhouette of altars and wells. */
export function octShape(w: number, d: number, c?: number, ox = 0, oy = 0): THREE.Shape {
  c = c ?? Math.min(w, d) * 0.3;
  const s = new THREE.Shape(), x0 = ox - w / 2, x1 = ox + w / 2, y0 = oy - d / 2, y1 = oy + d / 2;
  s.moveTo(x0 + c, y0); s.lineTo(x1 - c, y0); s.lineTo(x1, y0 + c); s.lineTo(x1, y1 - c);
  s.lineTo(x1 - c, y1); s.lineTo(x0 + c, y1); s.lineTo(x0, y1 - c); s.lineTo(x0, y0 + c); s.closePath();
  return s;
}

/** Extrude a 2D shape upward (Y) with an optional bevel; base sits at y=0. */
export function ext(shape: THREE.Shape, depth: number, bevel = 0, seg = 2): THREE.BufferGeometry {
  const g = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel, bevelSegments: seg, curveSegments: 24 });
  g.rotateX(-Math.PI / 2);
  g.translate(0, bevel, 0);
  return g;
}

export function ring(outer: THREE.Shape, inner: THREE.Shape): THREE.Shape {
  outer.holes.push(new THREE.Path(inner.getPoints()));
  return outer;
}

export function star4(R: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  for (let i = 0; i < 8; i++) {
    const a = i * Math.PI / 4 + Math.PI / 2, q = i % 2 ? r : R;
    if (i) s.lineTo(Math.cos(a) * q, Math.sin(a) * q); else s.moveTo(Math.cos(a) * q, Math.sin(a) * q);
  }
  s.closePath();
  return s;
}

export function crossShape(L: number, t: number): THREE.Shape {
  const s = new THREE.Shape(), a = L / 2, b = t / 2;
  ([[-b, a], [b, a], [b, b], [a, b], [a, -b], [b, -b], [b, -a], [-b, -a], [-b, -b], [-a, -b], [-a, b], [-b, b]] as const)
    .forEach(([x, y], i) => (i ? s.lineTo(x, y) : s.moveTo(x, y)));
  s.closePath();
  return s;
}

export function triShape(R: number): THREE.Shape {
  const s = new THREE.Shape();
  for (let i = 0; i < 3; i++) {
    const a = Math.PI / 2 + i * 2 * Math.PI / 3;
    if (i) s.lineTo(Math.cos(a) * R, Math.sin(a) * R); else s.moveTo(Math.cos(a) * R, Math.sin(a) * R);
  }
  s.closePath();
  return s;
}

/**
 * Triplanar-ish UVs in world units so tiled stone reads at a constant scale
 * no matter how a piece was sized. Must be called after the geometry is in world space.
 */
export function worldUV(geo: THREE.BufferGeometry, k: number) {
  const p = geo.attributes.position, n = geo.attributes.normal;
  if (!p || !n) return;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    let u: number, v: number;
    if (ay >= ax && ay >= az) { u = x; v = z; } else if (ax >= az) { u = z; v = y; } else { u = x; v = y; }
    uv[i * 2] = u * k + 0.37; uv[i * 2 + 1] = v * k + 0.11;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
